import { useCallback, useEffect, useMemo, useState } from 'react';
import api from '../../services/api';
import { loadApproved, loadCatalog } from '../nucleus/AppsCatalog';
import { plural } from '../../nucleus/format';
import { schemaLabel } from '../../utils/schemaUtils';

export { SHELVES, shelfOf, priceOf, shortPrice } from '../nucleus/AppsCatalog';

const WRITES = new Set(['create', 'write', 'update', 'edit', 'delete', 'purge', 'link']);
export const isWrite = (verb) => WRITES.has(String(verb));

// What an app will ask to reach, from its data lender request.
export function asksOf(app) {
    const items = app?.dlr_inputs || app?.data_lender_request?.dlr_inputs || [];
    return items.filter((i) => i && i.resource && i.verb).map((i) => ({
        verb: String(i.verb),
        what: i.label || (i.schema_ids?.length ? `${i.resource} · ${i.schema_ids.length} kind${i.schema_ids.length > 1 ? 's' : ''}` : i.resource),
    }));
}

export const statusOf = (row) => row?.ep?.status || 'active';

// WHAT AN APP ASKS FOR, kind by kind: each kind its request names, by its name
// (the store's listing resolves the schemas, dlr_schema_names), whether it would
// only read it or also write it, and the app's own reason where its request
// gives one. Anything else it asks for (relationships, plugins…) by its label.
const ANY_KIND = 'Every kind of node';
const resourceWord = (r) => String(r).replace(/[_-]+/g, ' ').replace(/^./, (c) => c.toUpperCase());
export function asksByKind(app) {
    const items = app?.dlr_inputs || app?.data_lender_request?.dlr_inputs || [];
    const names = app?.dlr_schema_names || {};
    const kinds = new Map();
    for (const i of items) {
        if (!i || !i.resource || !i.verb) continue;
        const ids = i.resource === 'nodes' && i.schema_ids?.length ? i.schema_ids : [null];
        for (const id of ids) {
            const known = id && names[String(id)];
            const name = known ? schemaLabel(known) : i.label || (i.resource === 'nodes' ? ANY_KIND : resourceWord(i.resource));
            const kind = kinds.get(name) || { name, write: false, reasons: [] };
            if (isWrite(i.verb)) kind.write = true;
            const why = i.reason || i.why || i.purpose;
            if (why && !kind.reasons.includes(why)) kind.reasons.push(why);
            kinds.set(name, kind);
        }
    }
    return [...kinds.values()];
}

// Who made it, as a name: the maker's own name, else the part of their address
// before the @ (never the address itself).
export const makerOf = (app) => app?.developer_name || String(app?.developer_email || '').split('@')[0] || null;

// what an app asks for, in two numbers: the kinds it would read, and the kinds it
// would write (create, edit, delete…). The contents are the contract's to show.
export function asksSummary(app) {
    // BY KIND, not by label: requests often carry no label, and ten kinds read one by
    // one counted as one. A kind is its schema id, else what the request names.
    const items = (app?.dlr_inputs || app?.data_lender_request?.dlr_inputs || []).filter((i) => i && i.resource && i.verb);
    const reads = new Set();
    const writes = new Set();
    for (const i of items) {
        const kinds = i.resource === 'nodes' && i.schema_ids?.length ? i.schema_ids.map(String) : [i.label || i.resource];
        for (const k of kinds) (isWrite(i.verb) ? writes : reads).add(k);
    }
    return { reads: reads.size, writes: writes.size, none: items.length === 0 };
}

// WHETHER IT IS YOURS ALREADY, in a word, or null: connected (live contract here),
// else paid for (subscribed, or bought once) without being connected.
export function ownedWord(entry) {
    const st = entry?.row ? statusOf(entry.row) : null;
    if (st === 'active' || st === 'paused') return 'Connected';
    if (entry?.app?.is_purchased) return entry.app.pricing?.model === 'subscription' ? 'Subscribed' : 'Bought';
    return null;
}

// how many people are connected to it, as the store says it
export function peopleWord(app) {
    if (app?.installs == null) return null;
    const n = app.installs;
    if (n === 0) return 'No one connected yet';
    return `${plural(n, 'person', 'people')} connected`;
}

// WHAT IT DID, IN ONE LINE: "462 operations: 460 reads, 1 created, 1 changed",
// a part said only when it happened. Its plugin runs are their own line, and only
// when there were any (`runsLine`).
export function opsLine(u) {
    const parts = [[u?.reads, 'read', 'reads'], [u?.created, 'created', 'created'], [u?.changed, 'changed', 'changed'], [u?.deleted, 'deleted', 'deleted']]
        .filter(([n]) => n > 0);
    const total = parts.reduce((sum, [n]) => sum + n, 0);
    if (!total) return 'No operations';
    return `${plural(total, 'operation')}: ${parts.map(([n, one, many]) => plural(n, one, many)).join(', ')}`;
}
export const runsLine = (u) => (u?.actuator_runs > 0 ? plural(u.actuator_runs, 'plugin run') : null);

const EMPTY_USAGE = { paid: null, reads: 0, created: 0, changed: 0, deleted: 0, node_ops: 0, actuator_runs: 0, proposals_waiting: 0, errors: 0, sign_ins: 0, sessions_live: 0, last_sign_in: null, last_active: null, by_day: [] };

// What each connected app did in the last 30 days (GET /app-shell/usage): one read
// for the whole page. `of(appId)` answers zeros for an app with nothing recorded.
export function useUsage(groupId, days = 30) {
    const [usage, setUsage] = useState(null);
    const load = useCallback(async () => {
        const q = new URLSearchParams({ days: String(days), ...(groupId ? { group_id: groupId } : {}) });
        const r = await api.request(`/app-shell/usage?${q}`).catch(() => null);
        setUsage(r || { apps: {}, days });
    }, [groupId, days]);
    useEffect(() => { load(); }, [load]);
    return useMemo(() => ({
        loaded: usage !== null,
        days: usage?.days || days,
        of: (id) => ({ ...EMPTY_USAGE, by_day: Array(usage?.days || days).fill(0), ...(usage?.apps?.[String(id)] || {}) }),
        // an agent's own share: its writes, and its reads through the ledger
        agent: (appId, key) => ({ ...EMPTY_USAGE, by_day: Array(usage?.days || days).fill(0), ...(usage?.agents?.[`${appId}:${key}`] || {}) }),
        reload: load,
    }), [usage, days, load]);
}

const epBase = (groupId) => (groupId ? `/groups/${groupId}/exposure-profiles` : '/exposure-profiles');

// The consent page, asked to connect (or edit, or renew) an app here; it comes
// back to the app, open on the Apps page.
export function grantHref(app, groupId, extra = '') {
    const back = `${window.location.origin}${groupId ? `/group/${groupId}` : ''}/apps?open=${app.id}`;
    return `/grant?client_id=${encodeURIComponent(app.client_id)}${groupId ? `&group_id=${groupId}` : ''}${extra}&redirect_uri=${encodeURIComponent(back)}`;
}

export const appsApi = {
    pause: (groupId, ep) => api.request(`${epBase(groupId)}/${ep.id}/pause`, { method: 'POST' }),
    resume: (groupId, ep) => api.request(`${epBase(groupId)}/${ep.id}/resume`, { method: 'POST' }),
    disconnect: (groupId, ep) => (groupId
        ? api.request(`/groups/${groupId}/exposure-profiles/${ep.id}`, { method: 'DELETE' })
        : api.revokeAuthorization(ep.id)),
};

// Every app the space can see, and where each stands in it:
//   connected  — a live grant here (active or paused)
//   lapsed     — only expired grants (the page says "Contract expired")
//   store      — the rest of the catalog
//   everything — every app there is, each with where it stands (the Store page):
//                the catalog, and the apps you hold that it does not list (a
//                plugin, an app no longer listed), so the store never misses one
export function useApps(groupId) {
    const [catalog, setCatalog] = useState(null);
    const [rows, setRows] = useState([]);
    const load = useCallback(async () => {
        const [cat, approved] = await Promise.all([loadCatalog(groupId), loadApproved(groupId)]);
        setCatalog(cat);
        setRows(approved);
    }, [groupId]);
    useEffect(() => { load(); }, [load]);

    return useMemo(() => {
        const byId = new Map((catalog || []).map((a) => [String(a.id), a]));
        const merged = (app) => ({ ...app, ...(byId.get(String(app.id)) || {}) });
        const per = new Map();
        for (const row of rows) {
            const id = String(row.app.id);
            if (!per.has(id)) per.set(id, { app: merged(row.app), rows: [] });
            per.get(id).rows.push(row);
        }
        const connected = [];
        const lapsed = [];
        for (const entry of per.values()) {
            const live = entry.rows.filter((r) => statusOf(r) !== 'expired');
            const standing = live.find((r) => statusOf(r) === 'active') || live.find((r) => statusOf(r) === 'paused');
            if (standing) connected.push({ ...entry, row: standing, live });
            else lapsed.push({ ...entry, row: entry.rows[0], live: [] });
        }
        const taken = new Set([...per.keys()]);
        const store = (catalog || []).filter((a) => !taken.has(String(a.id)));
        const byName = (a, b) => String(a.app?.name || a.name).localeCompare(String(b.app?.name || b.name));
        const held = new Map([...connected, ...lapsed].map((e) => [String(e.app.id), e]));
        const listed = new Set((catalog || []).map((a) => String(a.id)));
        const everything = [
            ...(catalog || []).map((a) => held.get(String(a.id)) || { app: a, row: null, rows: [], live: [] }),
            ...[...held.values()].filter((e) => !listed.has(String(e.app.id))),
        ];
        return {
            loaded: catalog !== null,
            catalog: catalog || [],
            connected: connected.sort(byName),
            lapsed: lapsed.sort(byName),
            store: store.sort(byName),
            everything: everything.sort(byName),
            find: (id) => connected.find((e) => String(e.app.id) === String(id))
                || lapsed.find((e) => String(e.app.id) === String(id))
                || (byId.get(String(id)) ? { app: byId.get(String(id)), rows: [], row: null, live: [] } : null),
            reload: load,
        };
    }, [catalog, rows, load]);
}

// Every app grant in this space, its agents' and their apps' own, in one read.
export async function loadAppGrants(groupId) {
    const r = await api.request(groupId ? `/groups/${groupId}/exposure-profiles` : '/exposure-profiles?entity_type=app').catch(() => null);
    return (r?.exposure_profiles || []).filter((ep) => ep && ep.entity_type === 'app' && !ep.revoked_at);
}

// The agents (an app's sub-entities) that hold a grant in this space.
export async function loadAgentGrants(groupId) {
    return (await loadAppGrants(groupId)).filter((ep) => ep.sub_entity_key);
}

// The consent page, asked to allow one of an app's agents here; it comes back
// to the agents.
export function agentGrantHref(app, key, groupId) {
    const back = `${window.location.origin}${groupId ? `/group/${groupId}` : ''}/apps/agents`;
    return `/grant?client_id=${encodeURIComponent(app.client_id)}&sub_entity_key=${encodeURIComponent(key)}${groupId ? `&group_id=${groupId}` : ''}&redirect_uri=${encodeURIComponent(back)}`;
}


// AN AGENT'S OWN NAME. A sub-entity's grant labels itself with its PARENT app's
// name (entity_label is the app's), so every persona of one app would read
// "Personas". Its own name is the app's roster entry for that key (the declared
// display name), else its key made readable. The app is always the second line.
const readable = (key) => {
    const words = String(key || '').replace(/[_-]+/g, ' ').trim();
    return words ? words.charAt(0).toUpperCase() + words.slice(1) : 'Agent';
};
export function agentNames(roster) {
    const named = new Map((roster || []).map((a) => [`${a.parent_app_id || a.app_id}:${a.sub_entity_key}`, a.display_name]));
    return (ep) => named.get(`${ep.entity_id}:${ep.sub_entity_key}`) || readable(ep.sub_entity_key);
}

// The agents your apps declare (whether or not one holds a grant here).
export async function loadDeclaredAgents(groupId) {
    const r = await (groupId ? api.getGroupSubEntityPlugins(groupId) : api.getMySubEntityPlugins()).catch(() => null);
    return (r?.plugins || []).filter((a) => a?.id);
}
